/* Web Mercator, for the two things the map camera needs it for: putting a point
 * in the middle of the *visible* map rather than the middle of the map, and
 * framing a set of points in a viewport that is partly covered by chrome.
 *
 * **`shiftLat` moved out of `web/src/workspace/g-map/map-canvas.ts`, it was not
 * copied** — the rule ADR 0032 decision 6 set for `byDistance`. The web has
 * shipped it since ADR 0012 §6; the native camera contract needs the identical
 * arithmetic, and two copies of a projection formula is exactly the drift
 * nobody would notice, because a wrong answer here looks like "the pin sits a
 * bit high" rather than like a bug.
 *
 * **Why offset the target rather than pan and correct.** ADR 0012 §6: "Rather
 * than pan and then correct — two motions — the *target centre* is offset
 * before the flight, in Web Mercator, so it is one continuous move."
 *
 * The unit of every pixel quantity here is whatever unit the map's zoom is
 * defined against — the world is 256 of them wide at zoom 0. That is CSS pixels
 * in the browser and **dp** on Android, which is not a detail: the same number
 * in device pixels would be out by the pixel ratio, 3 on the reference phone.
 * ADR 0033 decision 2 records that exact mistake being made, and measured away,
 * one function over.
 */

/** Latitude clamped to where `tan` is finite. Past this the poles send the
 *  camera to NaN, which renders as a blank map and no error. */
const MAX_RAD = 1.484;

/** Latitude -> Web Mercator y, normalised to 0 at the north pole and 1 at the
 *  south. The whole world is 1 unit tall, so `y * 256 * 2**zoom` is a pixel. */
export function mercatorY(lat: number): number {
  const rad = Math.max(-MAX_RAD, Math.min(MAX_RAD, (lat * Math.PI) / 180));
  return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2;
}

/** The inverse of `mercatorY`. */
export function latFromMercatorY(y: number): number {
  return (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;
}

/**
 * Move `lat` south by `deltaPx` at `zoom`, in Web Mercator.
 *
 * Positive `deltaPx` moves the *centre* south, which makes a point drawn at the
 * returned latitude appear that many units **higher** on screen. That is the
 * direction wanted whenever the bottom is more obscured than the top:
 *
 *     deltaPx = (bottomInset - topInset) / 2
 *
 * so the point lands in the middle of the band between them.
 */
export function shiftLat(lat: number, zoom: number, deltaPx: number): number {
  if (!deltaPx) return lat;
  return latFromMercatorY(mercatorY(lat) + deltaPx / (256 * Math.pow(2, zoom)));
}
